import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff } from "lucide-react";
import SharedLayout from "./SharedLayout";
import { useAuth } from "@/hooks/useAuth";
import GoogleLoginButton from "./GoogleLoginButton";
import { authService } from "@/services/api/auth.service";
import { authValidation } from "../../../utils/authValidation";

const LoginPage = ({ onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    mobile: "",
    type: "mobile",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [errors, setErrors] = useState({});

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    let finalValue = value;
    if (field === "mobile") {
      finalValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: finalValue,
    }));

    // Real-time validation
    validateField(field, finalValue);
  };

  const validateField = (name, value) => {
    let error = "";
    if (formData.type === "email") {
      if (name === "email") error = authValidation.validateEmail(value);
      if (name === "password") {
        if (!value) error = "Password is required";
      }
    } else {
      if (name === "mobile") error = authValidation.validateMobile(value);
    }
    
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  // Toast configuration options for consistent positioning
  const toastConfig = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let hasError = false;
    if (formData.type === "email") {
      const emailErr = validateField("email", formData.email);
      const passErr = validateField("password", formData.password);
      if (emailErr || passErr) hasError = true;
    } else {
      const mobileErr = validateField("mobile", formData.mobile);
      if (mobileErr) hasError = true;
    }

    if (hasError) {
      toast.error("Please fix the errors in the form before submitting");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let loginData;
      if (formData.type === "email") {
        loginData = {
          email: formData.email,
          password: formData.password,
          type: "email",
        };
      } else {
        loginData = {
          mobileNo: formData.mobile,
          type: "mobile",
        };
      }

      const response = await authService.login(loginData);

      if (formData.type === "mobile" || response.requiresOTP) {
        setView("verifyLoginOTP");
        toast.info("Please verify your mobile number with the OTP sent.", toastConfig);
      } else {
        if (response.status === "success") {
          const userData = response.data.user;
          const token = response.data.token;

          login(userData, token);
          toast.success("Login successful!", toastConfig);

          const pendingReferralCode = localStorage.getItem("pendingReferralCode");
          
          if (pendingReferralCode && userData.role !== "admin" && userData.role !== "expert") {
            navigate("/referral");
          } else {
            switch (userData.role) {
              case "admin":
                navigate("/admindashboard");
                break;
              case "expert":
                navigate("/expert-dashboard");
                break;
              default:
                navigate("/dashboard");
                break;
            }
          }
        } else {
          toast.error(response.message || "Login failed", toastConfig);
        }
      }
    } catch (error) {
      setError(error.message || "Login failed");
      toast.error(error.message || "Login failed", toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLoginOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authService.verifyLoginOTP({
        mobileNo: formData.mobile,
        otp: otpInput,
      });

      if (response.status === "success") {
        const userData = response.data.user;
        const token = response.data.token;

        login(userData, token);
        toast.success("Login successful!", toastConfig);

        const pendingReferralCode = localStorage.getItem("pendingReferralCode");
        
        if (pendingReferralCode && userData.role !== "admin" && userData.role !== "expert") {
          navigate("/referral");
        } else {
          switch (userData.role) {
            case "admin":
              navigate("/admindashboard");
              break;
            case "expert":
              navigate("/expert-dashboard");
              break;
            default:
              navigate("/dashboard");
              break;
          }
        }
      } else {
        toast.error(response.message || "Login failed", toastConfig);
      }
    } catch (error) {
      setError(error.message || "Verification failed");
      toast.error(error.message || "Verification failed", toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error("Please enter your email address", toastConfig);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authService.forgotPassword({
        email: formData.email,
      });
      toast.info("OTP sent to your email. Please check your inbox.", toastConfig);
      setView("verifyPasswordOTP");
    } catch (error) {
      setError(error.message || "Failed to send OTP");
      toast.error(error.message || "Failed to send OTP", toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPasswordOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authService.verifyPasswordResetOTP({
        email: formData.email,
        otp: otpInput,
      });

      if (response.status === "success") {
        setVerificationToken(response.data.verificationToken);
        toast.success("OTP verified successfully", toastConfig);
        setView("resetPassword");
      } else {
        toast.error(response.message || "OTP verification failed", toastConfig);
      }
    } catch (error) {
      setError(error.message || "OTP verification failed");
      toast.error(error.message || "OTP verification failed", toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match", toastConfig);
      setLoading(false);
      return;
    }

    const passwordValidation = (password) => {
      const hasUpperCase = /[A-Z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      if (password.length < 8) {
        toast.error("New password must be at least 8 characters long", toastConfig);
        return false;
      }
      if (!hasUpperCase) {
        toast.error("Password must contain at least one uppercase letter", toastConfig);
        return false;
      }
      if (!hasNumber) {
        toast.error("Password must contain at least one number", toastConfig);
        return false;
      }
      if (!hasSpecialChar) {
        toast.error("Password must contain at least one special character", toastConfig);
        return false;
      }
      return true;
    };

    if (!passwordValidation(formData.password)) {
      setLoading(false);
      return;
    }

    try {
      const response = await authService.resetPassword({
        email: formData.email,
        verificationToken: verificationToken,
        newPassword: formData.password,
      });

      toast.success("Password reset successful", toastConfig);
      setView("login");
    } catch (error) {
      setError(error.message || "Password reset failed");
      toast.error(error.message || "Password reset failed", toastConfig);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      type,
      email: "",
      password: "",
      mobile: "",
    }));
    setErrors({});
  };

  const renderLoginForm = () => (
    <div className="w-full max-w-md mx-auto">
      <div className="w-full space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#102147] mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-600">Sign in to continue to InvestHive</p>
        </div>

        <GoogleLoginButton disabled={loading} />

        <div className="flex items-center my-6">
          <div className="flex-1 border-t border-gray-200"></div>
          <span className="px-4 text-sm text-gray-500 font-medium">OR</span>
          <div className="flex-1 border-t border-gray-200"></div>
        </div>

        <form onSubmit={handleSubmit} className="w-full space-y-5">
          <div className="flex items-center justify-center gap-8 mb-6">
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  formData.type === "mobile"
                    ? "border-[#44D2F6] bg-[#44D2F6]/10"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onClick={() => handleTypeChange("mobile")}
              >
                {formData.type === "mobile" && (
                  <div className="w-2.5 h-2.5 rounded-full bg-[#44D2F6]"></div>
                )}
              </div>
              <span className="text-gray-700 font-medium">Mobile</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer group">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  formData.type === "email"
                    ? "border-[#44D2F6] bg-[#44D2F6]/10"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onClick={() => handleTypeChange("email")}
              >
                {formData.type === "email" && (
                  <div className="w-2.5 h-2.5 rounded-full bg-[#44D2F6]"></div>
                )}
              </div>
              <span className="text-gray-700 font-medium">Email</span>
            </label>
          </div>

          {formData.type === "email" ? (
            <>
              <div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    handleInputChange("email", e.target.value)
                  }
                  className={`w-full px-4 py-3.5 rounded-lg border-2 focus:ring-4 transition-all duration-200 outline-none ${
                    errors.email ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-[#44D2F6] focus:ring-[#44D2F6]/20"
                  }`}
                  required
                />
                {errors.email && <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.email}</p>}
              </div>
              <div className="space-y-1">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    className={`w-full px-4 py-3.5 rounded-lg border-2 focus:ring-4 transition-all duration-200 outline-none pr-12 ${
                      errors.password ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-[#44D2F6] focus:ring-[#44D2F6]/20"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.password}</p>}
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setView("forgotPassword")}
                  className="text-sm text-[#44D2F6] hover:text-[#0C7FA5] font-medium transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            </>
          ) : (
            <div>
              <input
                type="tel"
                placeholder="Mobile Number"
                value={formData.mobile}
                onChange={(e) =>
                  handleInputChange("mobile", e.target.value)
                }
                className={`w-full px-4 py-3.5 rounded-lg border-2 focus:ring-4 transition-all duration-200 outline-none ${
                    errors.mobile ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-[#44D2F6] focus:ring-[#44D2F6]/20"
                }`}
                required
              />
              {errors.mobile && <p className="text-xs text-red-500 mt-1 ml-1 font-medium">{errors.mobile}</p>}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading
              ? "Signing In..."
              : formData.type === "mobile"
              ? "Get OTP"
              : "Sign In"}
          </button>

          <p className="text-center text-gray-600">
            Don't have an account?{" "}
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-[#44D2F6] hover:text-[#0C7FA5] font-semibold transition-colors"
            >
              Sign Up
            </button>
          </p>
        </form>
      </div>
    </div>
  );

  const renderVerifyLoginOTP = () => (
    <div className="w-full max-w-md mx-auto">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm font-medium border border-red-100">{error}</div>
      )}
      <div className="w-full space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#102147] mb-2">
            Verify OTP
          </h1>
          <p className="text-gray-600">Enter the code sent to your mobile</p>
        </div>

        <form onSubmit={handleVerifyLoginOTP} className="w-full space-y-5">
          <div>
            <input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              className="w-full px-4 py-3.5 rounded-lg border-2 border-gray-200 focus:border-[#44D2F6] focus:ring-4 focus:ring-[#44D2F6]/20 transition-all duration-200 outline-none text-center text-lg tracking-widest"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? "Verifying..." : "Verify & Login"}
          </button>

          <p className="text-center text-gray-600">
            Having trouble?{" "}
            <button
              type="button"
              onClick={() => setView("login")}
              className="text-[#44D2F6] hover:text-[#0C7FA5] font-semibold transition-colors"
            >
              Back to Login
            </button>
          </p>
        </form>
      </div>
    </div>
  );

  const renderForgotPasswordForm = () => (
    <div className="w-full max-w-md mx-auto">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm font-medium border border-red-100">{error}</div>
      )}
      <div className="w-full space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#102147] mb-2">
            Forgot Password
          </h1>
          <p className="text-gray-600">Enter your email to receive a code</p>
        </div>

        <form onSubmit={handleForgotPassword} className="w-full space-y-5">
          <div>
            <input
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              className="w-full px-4 py-3.5 rounded-lg border-2 border-gray-200 focus:border-[#44D2F6] focus:ring-4 focus:ring-[#44D2F6]/20 transition-all duration-200 outline-none"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? "Sending..." : "Send OTP"}
          </button>

          <p className="text-center text-gray-600">
            Remember your password?{" "}
            <button
              type="button"
              onClick={() => setView("login")}
              className="text-[#44D2F6] hover:text-[#0C7FA5] font-semibold transition-colors"
            >
              Back to Login
            </button>
          </p>
        </form>
      </div>
    </div>
  );

  const renderVerifyPasswordOTP = () => (
    <div className="w-full max-w-md mx-auto">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm font-medium border border-red-100">{error}</div>
      )}
      <div className="w-full space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#102147] mb-2">
            Verify OTP
          </h1>
          <p className="text-gray-600">Enter the code sent to {formData.email}</p>
        </div>

        <form onSubmit={handleVerifyPasswordOTP} className="w-full space-y-5">
          <div>
            <input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              className="w-full px-4 py-3.5 rounded-lg border-2 border-gray-200 focus:border-[#44D2F6] focus:ring-4 focus:ring-[#44D2F6]/20 transition-all duration-200 outline-none text-center text-lg tracking-widest"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>

          <div className="text-center space-y-3">
            <p className="text-gray-600">
              Didn't receive the code?{" "}
              <button
                type="button"
                onClick={() => setView("forgotPassword")}
                className="text-[#44D2F6] hover:text-[#0C7FA5] font-semibold transition-colors"
              >
                Resend OTP
              </button>
            </p>

            <button
              type="button"
              onClick={() => setView("login")}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderResetPasswordForm = () => (
    <div className="w-full max-w-md mx-auto">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm font-medium border border-red-100">{error}</div>
      )}
      <div className="w-full space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#102147] mb-2">
            Reset Password
          </h1>
          <p className="text-gray-600">Create a new password for your account</p>
        </div>

        <form onSubmit={handleResetPassword} className="w-full space-y-5">
          <div className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                className="w-full px-4 py-3.5 rounded-lg border-2 border-gray-200 focus:border-[#44D2F6] focus:ring-4 focus:ring-[#44D2F6]/20 transition-all duration-200 outline-none pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))
                }
                className="w-full px-4 py-3.5 rounded-lg border-2 border-gray-200 focus:border-[#44D2F6] focus:ring-4 focus:ring-[#44D2F6]/20 transition-all duration-200 outline-none pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Password must be at least 8 characters long, include an uppercase
            letter, a number, and a special character
          </p>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>

          <p className="text-center text-gray-600">
            <button
              type="button"
              onClick={() => setView("login")}
              className="text-[#44D2F6] hover:text-[#0C7FA5] font-semibold transition-colors"
            >
              Back to Login
            </button>
          </p>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 min-h-[40vh] lg:min-h-screen">
        <SharedLayout />
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 py-8 lg:py-16 min-h-[60vh] lg:min-h-screen">
        <div className="w-full max-w-md">
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            draggable
            newestOnTop={false}
            rtl={false}
            theme="light"
          />
          {view === "login" && renderLoginForm()}
          {view === "forgotPassword" && renderForgotPasswordForm()}
          {view === "verifyPasswordOTP" && renderVerifyPasswordOTP()}
          {view === "resetPassword" && renderResetPasswordForm()}
          {view === "verifyLoginOTP" && renderVerifyLoginOTP()}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;